import { useCallback, useEffect, useState } from "react";
import { Logger } from "@/lib/logger";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import { isIosWebkit, isOwnAppWebView, isStandalone } from "@/lib/device";
import { getMessagingInstance, requestFcmToken } from "@/lib/push/messaging";
import { registerPushToken, deletePushToken } from "@/services/push-token.service";
import { ApiError } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";

const log = new Logger("Push");

export type PushSupportState = "checking" | "unsupported" | "ios-needs-install" | "supported";

/** Statut de permission navigateur, plus l'état transitoire "requesting" côté UI. */
export type PushPermissionState = NotificationPermission | "requesting";

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // stockage bloqué : rien à persister, l'utilisateur redemandera à la prochaine session
  }
}

function deviceName(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Chrome\//.test(ua)
      ? "Chrome"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Navigateur";
  const os = /Windows/.test(ua)
    ? "Windows"
    : /Mac OS X/.test(ua)
      ? "macOS"
      : /Android/.test(ua)
        ? "Android"
        : /iPhone|iPad|iPod/.test(ua)
          ? "iOS"
          : "";
  return os ? `${browser} – ${os}` : browser;
}

/**
 * Enregistre le jeton FCM courant auprès du backend, seulement s'il a changé depuis le dernier
 * envoi (l'endpoint est idempotent mais autant éviter l'appel réseau à chaque montage).
 *
 * Révoque l'ancien jeton connu **avant** d'enregistrer le nouveau, quand FCM en a émis un
 * différent (rotation naturelle du token — fréquente sur Android : mise à jour de Chrome,
 * changement réseau…). Sans ça, l'ancien reste actif côté backend indéfiniment : `POST
 * /user/push-tokens` n'écrase rien, il ajoute un jeton par appareil. Deux jetons actifs pour le
 * même téléphone reçoivent chacun leur propre envoi FCM pour une seule notification — la
 * notification Android est donc dupliquée, le regroupement par `tag` du service worker
 * n'intervenant qu'entre notifications reçues via le *même* jeton.
 *
 * Gère le 403 « email non vérifié » (middleware `verified`) en silence : l'appelant est censé
 * réessayer une fois `email_verified_at` renseigné (cf. l'effet de retry dans le hook ci-dessous).
 */
async function syncTokenWithBackend(token: string): Promise<boolean> {
  const lastSent = safeGet(STORAGE_KEYS.PUSH_TOKEN_VALUE);
  const lastSentId = safeGet(STORAGE_KEYS.PUSH_TOKEN_ID);
  if (lastSent === token && lastSentId) {
    return true;
  }

  if (lastSentId && lastSent && lastSent !== token) {
    try {
      await deletePushToken(Number(lastSentId));
    } catch (err) {
      // Best-effort : un échec ici ne doit pas empêcher l'enregistrement du nouveau jeton, qui
      // reste l'essentiel. Le back nettoie de toute façon les jetons inactifs après 30 jours
      // (`push-tokens:prune`) — le pire résultat est un doublon transitoire, pas une perte.
      log.warn("Échec de révocation de l'ancien jeton push avant rotation", err);
    }
  }

  try {
    const res = await registerPushToken({
      token,
      platform: "web",
      device_name: deviceName(),
      language: "fr",
    });
    safeSet(STORAGE_KEYS.PUSH_TOKEN_ID, String(res.data.id));
    safeSet(STORAGE_KEYS.PUSH_TOKEN_VALUE, token);
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) {
      log.debug("Enregistrement du jeton push différé : email non vérifié.");
      return false;
    }
    log.warn("Échec d'enregistrement du jeton push", err);
    return false;
  }
}

/**
 * État + actions pour les notifications push Web (FCM). Encapsule toute la séquence du
 * §3.2 de `docs/PWA_PUSH_NOTIFICATIONS.md` : support → permission → jeton → enregistrement.
 *
 * `enabled: false` (ex. compte pas encore vérifié) désactive uniquement la synchronisation
 * silencieuse automatique — `requestPermission()` reste appelable directement depuis un toggle.
 */
export function usePushNotifications() {
  const token = useAuthStore((s) => s.token);
  const emailVerified = useAuthStore((s) => !!s.user?.email_verified_at);

  const [support, setSupport] = useState<PushSupportState>("checking");
  const [permission, setPermission] = useState<PushPermissionState>(
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );
  const [isRegistered, setIsRegistered] = useState(() => !!safeGet(STORAGE_KEYS.PUSH_TOKEN_ID));

  // 1. Détection du support — iOS/iPadOS hors PWA installée n'a pas Web Push du tout.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (typeof Notification === "undefined" || typeof window === "undefined") {
        if (!cancelled) setSupport("unsupported");
        return;
      }
      // App mobile Flutter (WebView `succeschoolapp`) : le jeton push est natif (FCM/APNs),
      // enregistré côté Flutter directement auprès du même endpoint `/user/push-tokens` avec
      // `platform: "ios" | "android"`. Le jeton *web* ici serait un second jeton pour le même
      // appareil — même défaut de duplication que la rotation décrite dans `syncTokenWithBackend`,
      // mais permanent au lieu de transitoire (deux jetons actifs en continu, chaque notification
      // reçue deux fois).
      if (isOwnAppWebView()) {
        if (!cancelled) setSupport("unsupported");
        return;
      }
      if (isIosWebkit() && !isStandalone()) {
        if (!cancelled) setSupport("ios-needs-install");
        return;
      }
      const messaging = await getMessagingInstance();
      if (!cancelled) setSupport(messaging ? "supported" : "unsupported");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 2. Synchronisation silencieuse : permission déjà accordée → (re)vérifie le jeton à chaque
  // connexion, sans jamais redemander la permission ni solliciter l'utilisateur.
  useEffect(() => {
    if (support !== "supported" || !token || !emailVerified) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

    let cancelled = false;
    (async () => {
      const fcmToken = await requestFcmToken();
      if (cancelled || !fcmToken) return;
      const ok = await syncTokenWithBackend(fcmToken);
      if (!cancelled && ok) setIsRegistered(true);
    })();
    return () => {
      cancelled = true;
    };
    // `emailVerified` en dépendance : couvre le retry après vérification (§checklist,
    // "Retry de l'enregistrement une fois l'email vérifié").
  }, [support, token, emailVerified]);

  /** Déclenché par une action utilisateur explicite (toggle) — jamais au chargement de la page. */
  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return false;

    if (Notification.permission === "denied") {
      log.debug("Permission notifications refusée précédemment — pas de nouvelle demande.");
      return false;
    }

    setPermission("requesting");
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result !== "granted") return false;

    const fcmToken = await requestFcmToken();
    if (!fcmToken) return false;

    const ok = await syncTokenWithBackend(fcmToken);
    setIsRegistered(ok);
    return ok;
  }, []);

  return {
    /** "checking" | "unsupported" | "ios-needs-install" | "supported" */
    support,
    /** Permission Notification navigateur, plus "requesting" pendant la demande. */
    permission,
    isRegistered,
    requestPermission,
  };
}
